'use client';

import { useId, type ReactNode } from 'react';

import { cn } from '../lib/utils';
import { Switch } from '../shadcn/switch';

/**
 * A single settings row: label + helper text on the left, a Switch on the
 * right. Mirrors the divider-separated settings pattern used by Linear /
 * Vercel / GoFundMe (Mobbin) — no per-row bordered box, no centered content.
 *
 * Presentational only: the caller owns the value (typically a react-hook-form
 * `field`), so the same row works inside or outside a form. Group several in a
 * `divide-y` container to get the stacked settings-list look.
 *
 * Canonical setting-toggle row for the whole app — adopt this instead of
 * hand-rolling a `flex items-center justify-between … border p-4` wrapper
 * around a bare `<Switch>` (Critical Rule #8, single edit surface).
 */
export function SettingToggleRow({
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  className,
}: {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();

  return (
    <div
      className={cn('flex items-center justify-between gap-6 py-4', className)}
    >
      <div className="space-y-0.5">
        <label
          htmlFor={id}
          className="cursor-pointer text-sm leading-none font-medium"
        >
          {label}
        </label>
        {description ? (
          <p className="text-muted-foreground text-sm">{description}</p>
        ) : null}
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="shrink-0"
      />
    </div>
  );
}
